// Pure, client-side onboarding-progress model.
//
// The percent shown to a logged-in user is derived entirely from the
// dashboard/v1/summary/ payload (plus locally-latched flags, see
// onboardingProgressStore) rather than any single server "onboarded" field,
// so it can move in small, granular steps instead of a single done/not-done
// switch.

export type StepKey =
  | 'profile'
  | 'username'
  | 'connect_accounts'
  | 'player_linked';

export interface OnboardingStepConfig {
  key: StepKey;
  label: string;
  icon: string; // FontAwesome5 name
  route: string; // ScreenName value; '' = no navigation (engagement-only step)
  // If present, the step is "done" when this predicate is true for the summary
  // user (data.data.user). Steps without it are flag-only (latched locally).
  summaryDone?: (s: any) => boolean;
}

export interface StepStatus extends OnboardingStepConfig {
  done: boolean;
}

export interface OnboardingProgress {
  steps: StepStatus[];
  completed: number;
  total: number;
  percent: number; // 0..100 integer
}

// Backend-assigned username shapes, never typed by a human:
//  - the account phone itself / any bare 10+ digit string (legacy)
//  - otp_<digits>… (partner-product OTP flow, same cc_users table)
//  - user_<digits> (chatbot verify_otp generator, pre reddit-style)
//  - Adjective-Noun-123 (chatbot reddit-style generator) — matches a chosen
//    name of the same shape too; the local keep-latch (flags.username) is what
//    records the user's explicit "keep", so a false positive costs one prompt.
export const isGeneratedUsername = (unameRaw: any, phoneRaw: any): boolean => {
  const uname = String(unameRaw ?? '').trim();
  const phone = String(phoneRaw ?? '').trim();
  if (!uname) return true;
  return (
    uname === phone ||
    /^\d{10,}$/.test(uname) ||
    /^otp_\d+/i.test(uname) ||
    /^user_\d+$/i.test(uname) ||
    /^[A-Za-z]+-[A-Za-z]+-\d{1,5}$/.test(uname)
  );
};

export const ONBOARDING_STEPS: OnboardingStepConfig[] = [
  {
    key: 'profile',
    label: 'Create your player profile',
    icon: 'user-edit',
    route: 'Settings',
    summaryDone: s => !!s?.user?.first_name && !!s?.user?.pincode,
  },
  {
    key: 'username',
    label: 'Pick your username',
    icon: 'id-badge',
    // '' = no navigation (see the `route` field comment above): the keep-
    // or-change action lives inline in ChecklistBody under this row instead
    // of on Settings, and an empty route also keeps this step out of
    // `steps.find(s => !s.done && s.route)`, so it's never the next-step/
    // primary-CTA target either.
    route: '',
    summaryDone: s => {
      const u = String(s?.user?.username ?? '').trim();
      return !!u && !isGeneratedUsername(u, s?.user?.mobile_number);
    },
  },
  {
    key: 'connect_accounts',
    label: 'Connect Lichess / Chess.com',
    icon: 'link',
    route: 'Settings',
    summaryDone: s =>
      !!(s?.user?.lic_name && String(s.user.lic_name).trim()) ||
      !!(s?.user?.cdc_name && String(s.user.cdc_name).trim()),
  },
  {
    key: 'player_linked',
    label: 'Finish linking your account',
    icon: 'user-check',
    route: 'Settings',
    // CCUser.player links to a tournament_notification_registrations row.
    // 0 means unlinked -- most commonly a CRM-created pseudo player from the
    // original CSOC purchase that was never connected to this login. ANY
    // Settings profile save (even with nothing changed) re-links it
    // automatically server-side (resolve_or_create_player, see
    // dashboard/csoc_setup.py) -- there's no separate action to take beyond
    // saving the profile again, which is why this step's route is just
    // Settings like `profile`.
    summaryDone: s => !!s?.user?.player,
  },
];

export function computeOnboardingSteps(
  summary: any,
  flags: Partial<Record<StepKey, boolean>> = {},
): OnboardingProgress {
  const steps: StepStatus[] = ONBOARDING_STEPS.map(cfg => ({
    ...cfg,
    done:
      !!flags[cfg.key] ||
      (cfg.summaryDone ? !!cfg.summaryDone(summary) : false),
  }));
  const completed = steps.filter(s => s.done).length;
  const total = steps.length;
  const percent = total === 0 ? 0 : Math.round((completed / total) * 100);
  return {steps, completed, total, percent};
}
